import { CSSProperties } from "react";

import styled, { css } from "styled-components";

import { IconNoviMarkWhite } from "components/icons";

import { PRODUCT_ACCENT, Product } from "./products";

/**
 * Box with an arrow leaving it: this opens a new tab.
 *
 * Carries its own accessible name rather than being decorative, because it is
 * the only thing on the tile saying a tab will open.
 */
function NewTabIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="Opens in a new tab">
      <path d="M14 4h6v6" />
      <path d="M20 4l-8.5 8.5" />
      <path d="M19 14.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4.5" />
    </svg>
  );
}

/** Labels the tile grid for assistive tech; the visible heading carries it. */
const HEADING_ID = "novi-app-switcher-heading";

interface AppSwitcherProps {
  /** The products to offer. Already filtered to what this user can reach. */
  products: Product[];
  /** `id` of the product EVA is, so its tile is marked as the open one. */
  currentId: string;
  onClose: () => void;
}

/**
 * The Novi application switcher: one tile per product the user can reach, the
 * open one marked in its own colour.
 *
 * Ported from novi-apps' `@novi/portal` `AppSwitcher` rather than copied — that
 * one is CSS modules against `@novi/design`'s `EvaPopover`, this is
 * styled-components inside antd's, which is what the rest of EVA's navbar uses.
 * What carried over is the behaviour: reachable products only, the current app
 * marked rather than hidden, and every product opening in a new tab.
 *
 * Dismissal is antd's: `trigger="click"` gives outside-click, and the trigger
 * handles Escape. This renders the panel's contents only.
 */
function AppSwitcher({ products, currentId, onClose }: AppSwitcherProps) {
  /*
   * Every product opens in a new tab, not just the ones on another host.
   *
   * Switching used to replace this tab for the apex-served apps and open a new
   * one for the rest, so where a reader came back to depended on which tile
   * they picked. EVA is the kind of app people leave loaded — a filter set, a
   * dashboard mid-edit — and wanting a second app should not cost them that.
   *
   * The current app is the exception, and only because there is nothing to
   * switch to: a second copy of the page you are already on is not a
   * destination. That one tile is a button rather than a link, because a link
   * that goes nowhere is worse than a control saying you are already here.
   */
  const opensATab = (product: Product) => product.id !== currentId;

  return (
    <Card>
      <Heading id={HEADING_ID}>Switch Novi application</Heading>
      <Grid role="group" aria-labelledby={HEADING_ID}>
        {products.map((product) => {
          const accent = PRODUCT_ACCENT[product.color];
          // Custom properties rather than direct background/border, so the hover
          // and active rules stay in the stylesheet rather than moving inline.
          const tint = {
            "--tile-solid": accent.solid,
            "--tile-soft": accent.soft,
            "--tile-line": accent.line
          } as CSSProperties;

          const body = (
            <>
              <Badge aria-hidden="true">
                <IconNoviMarkWhite size={24} />
              </Badge>
              <Name>
                {product.name}
                {opensATab(product) && (
                  <NewTabMark>
                    <NewTabIcon />
                  </NewTabMark>
                )}
              </Name>
              <Blurb>{product.blurb}</Blurb>
            </>
          );

          /*
           * A real anchor, not a button calling window.open.
           *
           * The mark says a tab will open; the href is what lets a reader act
           * on that. Right-click gives them Open in new tab, Copy link and the
           * rest, middle-click and cmd-click work, and the status bar shows
           * where they are going. A button had none of that and there was
           * nothing to right-click.
           *
           * `noopener` is what window.open carried and is the part that
           * matters: it denies the opened page a handle on this one.
           * `noreferrer` comes along because these are sibling Novi apps and
           * none of them needs to be told which one sent the reader.
           */
          return opensATab(product) ? (
            <TileLink
              key={product.id}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              style={tint}
              $active={false}
              data-testid={`app-switcher-tile-${product.id}`}
              onClick={onClose}>
              {body}
            </TileLink>
          ) : (
            <TileButton
              key={product.id}
              type="button"
              style={tint}
              $active
              aria-current="page"
              data-testid={`app-switcher-tile-${product.id}`}
              onClick={onClose}>
              {body}
            </TileButton>
          );
        })}
      </Grid>
    </Card>
  );
}

export default AppSwitcher;

const Card = styled.div`
  width: 460px;
  padding: 18px 16px;
`;

const Heading = styled.h2`
  margin: 0 0 var(--space-2);
  color: var(--color-text-60);
  font-size: 12px;
  font-weight: var(--fontWeightMedium);
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: var(--space-2);
`;

// Shared by both tile shapes so the link and the current-app button are the
// same object to a reader. The open product is dressed in its own colour: both
// the wash and the border come from the product, set inline as custom
// properties — see PRODUCT_ACCENT for why those are hexes and not EVA tokens.
const tile = css<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2);
  border: 1px solid ${(props) => (props.$active ? "var(--tile-line)" : "transparent")};
  border-radius: calc(var(--border-radius) + 2px);
  background: ${(props) => (props.$active ? "var(--tile-soft)" : "transparent")};
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  /* A tile is a link now, and should not look like body text with a rule under
     it: the tile is the target, so underlining the name says the name is. */
  text-decoration: none;
  text-align: center;

  &:hover {
    background: var(--tile-soft);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

const TileLink = styled.a<{ $active: boolean }>`
  ${tile}
`;

const TileButton = styled.button<{ $active: boolean }>`
  ${tile}
`;

const Badge = styled.span`
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: calc(var(--border-radius) + 2px);
  margin-bottom: var(--space-2);
  background: var(--tile-solid);
`;

const Name = styled.span`
  color: var(--color-text);
  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  line-height: 1.3;
`;

const Blurb = styled.span`
  color: var(--color-text-60);
  font-size: 12px;
  line-height: 1.3;
`;

const NewTabMark = styled.span`
  display: inline-flex;
  margin-left: var(--space-1);
  color: var(--color-text-40);
  vertical-align: middle;
`;
